import { ResponsiveBar } from '@nivo/bar'
import theme from "./theme";
import PropTypes from "prop-types";
import lang from "../../core/lang";
import dom from "../../core/dom";
import {useTheme} from "@material-ui/core";
import palette from "../../core/palette";

const DEFAULT_LEGEND = {
    anchor: 'bottom-right',
    direction: 'column',
    // justify: false,
    translateX: 20,
    // translateY: 0,
    itemsSpacing: 2,
    itemWidth: 0,
    itemHeight: 28,
    itemDirection: 'left-to-right',
    itemOpacity: 0.85,
    symbolSize: 20,
    effects: [
        {
            on: 'hover',
            style: {
                itemOpacity: 1
            }
        }
    ]
};

const DEFAULT_COLORS = ['#4e79a7', '#f28e2b', '#e15759', '#76b7b2', '#59a14f', '#edc948', '#b07aa1', '#ff9da7', '#9c755f',  '#bab0ac'];

function resolveTheme(custom = {}, {fontSize, muiTheme}) {
    const resolved = lang.clone({...theme, ...custom});
    resolved.text.fill = muiTheme.palette.text.primary;
    resolved.axis.domain.line.stroke = muiTheme.palette.text.primary;
    resolved.axis.ticks.line.stroke = muiTheme.palette.text.primary;
    resolved.grid.line.stroke = palette.darker(muiTheme.palette.background.paper, 1);
    if(fontSize) {
        resolved.text.fontSize = dom.scaleFont(fontSize, 0.8);
        resolved.axis.legend.text.fontSize = fontSize;
        resolved.axis.ticks.text.fontSize = dom.scaleFont(fontSize, 0.85);
        resolved.legends.text.fontSize = dom.scaleFont(fontSize, 0.8);
        resolved.tooltip.container.fontSize = dom.scaleFont(fontSize, 0.85);
    }
    return resolved;
}

function BarChart(props) {
    const { fontSize, data, indexBy, valueBy, margin, padding, innerPadding,
        enableGridX, enableGridY, enableLabel, enableLegend, isInteractive,
        groupMode, layout, labelSkipHeight, labelSkipWidth, layers, enableTotals} = props;
    const muiTheme = useTheme();
    const theme = resolveTheme(props.theme, {fontSize, muiTheme});
    const axisLeft = props.axisLeft ? {tickSize: 5, tickPadding: 5, legendPosition: 'middle', legendOffset: -60, ...props.axisLeft} : null;
    const axisBottom = props.axisBottom ? {tickSize: 5, tickPadding: 5, legendPosition: 'middle', legendOffset: 48, ...props.axisBottom} : null;
    const axisRight = props.axisRight ? {tickSize: 5, tickPadding: 5, legendPosition: 'middle', ...props.axisRight} : null;
    const axisTop = props.axisTop ? {tickSize: 5, tickPadding: 5, legendPosition: 'middle', ...props.axisTop} : null;

    const legends = transformLegends(enableLegend);
    
    const keys = lang.isArray(valueBy) ? valueBy : [valueBy];

    // 移除預設的 groupMode 設定，只有在 groupMode 不存在且數列只有一個時，才會讓該數列顯示不同顏色。
    const colors = (keys.length === 1 && !groupMode) ? (params) => computeColor(params, props.colors) : props.colors;

    return (
        <ResponsiveBar
            data={data}
            keys={keys}
            indexBy={indexBy}
            margin={margin}
            padding={padding}
            innerPadding={innerPadding}
            groupMode={groupMode}
            layout={layout}
            theme={theme}
            colors={colors}
            axisTop={axisTop}
            axisRight={axisRight}
            axisBottom={axisBottom}
            axisLeft={axisLeft}
            enableGridX={enableGridX}
            enableGridY={enableGridY}
            enableLabel={enableLabel}
            labelSkipHeight={labelSkipHeight}
            labelSkipWidth={labelSkipWidth}
            labelTextColor={{
                from: 'color',
                modifiers: [
                    [
                        'darker',
                        1.6
                    ]
                ]
            }}
            isInteractive={isInteractive}
            legends={legends}
            layers={layers}
            enableTotals={enableTotals}
        />
    )
}

function computeColor({id, index, indexValue, value, data}, colors) {
    const colorIndex = index % colors.length;
    return colors[colorIndex];
}

function transformLegends(enableLegend) {
    return enableLegend ? [DEFAULT_LEGEND] : undefined;
}

BarChart.propTypes = {
    theme: PropTypes.object,
    fontSize: PropTypes.string,
    data: PropTypes.arrayOf(PropTypes.object).isRequired,
    indexBy: PropTypes.string,
    valueBy: PropTypes.oneOfType([
       PropTypes.string,
       PropTypes.arrayOf(PropTypes.string)
    ]),
    margin: PropTypes.object,
    padding: PropTypes.number,
    innerPadding: PropTypes.number,
    axisBottom: PropTypes.shape({
        legend: PropTypes.string,
        legendPosition: PropTypes.string,
        tickRotation: PropTypes.number,
        truncateTickAt: PropTypes.number
    }),
    axisLeft: PropTypes.shape({
        legend: PropTypes.string,
        legendPosition: PropTypes.string,
        tickRotation: PropTypes.number,
        truncateTickAt: PropTypes.number
    }),
    axisRight: PropTypes.shape({
        legend: PropTypes.string,
        legendPosition: PropTypes.string,
        tickRotation: PropTypes.number,
        truncateTickAt: PropTypes.number
    }),
    axisTop: PropTypes.shape({
        legend: PropTypes.string,
        legendPosition: PropTypes.string,
        tickRotation: PropTypes.number,
        truncateTickAt: PropTypes.number
    }),
    enableGridX: PropTypes.bool,
    enableGridY: PropTypes.bool,
    colors: PropTypes.arrayOf(PropTypes.string),
    groupMode: PropTypes.oneOf(['stacked', 'grouped']),
    layout: PropTypes.oneOf(['vertical', 'horizontal']),
    enableLabel: PropTypes.bool,
    isInteractive: PropTypes.bool,
    enableLegend: PropTypes.bool,
    labelSkipHeight: PropTypes.number,
    labelSkipWidth: PropTypes.number,
    enableTotals: PropTypes.bool,
    layers: PropTypes.arrayOf(PropTypes.oneOfType([
        PropTypes.oneOf(['grid', 'axes', 'bars', 'totals', 'markers', 'legends', 'annotations']),
        PropTypes.func
    ]))
}

BarChart.defaultProps = {
    indexBy: 'id',
    valueBy: 'value',
    enableGridX: false,
    enableGridY: true,
    colors: DEFAULT_COLORS,
    layout: 'vertical',
    enableLabel: true,
    isInteractive: true,
    enableLegend: false,
    labelSkipHeight: 16,
    labelSkipWidth: 48
}

export default BarChart;